import React from 'react';
import { Button } from '@/components/ui/button';
import { CreditCard, Clock, FileText, CheckCircle } from 'lucide-react';
import IntellendLogo from '@/components/IntellendLogo';

interface LandingProps {
  value?: any;
  onChange?: (value: any) => void;
  disabled?: boolean;
  onNext?: () => void;
  isLoading?: boolean;
}

/**
 * Landing - Landing page component for non-captive term loan journey
 *
 * This component displays the value proposition and features of the loan product.
 * Mobile-first design matching the provided screenshot.
 */
const Landing: React.FC<LandingProps> = ({
  onNext,
  disabled = false,
  isLoading = false
}) => {
  const handleApplyNow = () => {
    if (onNext && !disabled && !isLoading) {
      onNext();
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Hero Section with Green Background */}
      <div className="bg-intellend-green text-white px-4 py-8 flex flex-col items-center">
        {/* Logo */}
        <div className="w-full max-w-md mb-8">
          <div className="bg-white rounded-lg px-2 py-1 inline-block">
            <IntellendLogo size="sm" />
          </div>
        </div>

        {/* Hero Content */}
        <div className="w-full max-w-md text-center space-y-4">
          <h1 className="text-2xl md:text-3xl font-bold leading-tight">
            Financial Solutions for Indian MSMEs
          </h1>
          <p className="text-base md:text-lg text-white/90">
            Access business loans with transparent terms and competitive rates
          </p>
          
          {/* Get Started Button */}
          <div className="pt-4">
            <Button
              onClick={handleApplyNow}
              disabled={disabled || isLoading}
              size="lg"
              className="bg-intellend-gold hover:bg-[#b8934d] text-white px-8 py-6 text-base md:text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Loading...
                </>
              ) : (
                'Get Started'
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Why Choose Section */}
      <div className="flex-1 bg-gray-50 px-4 py-8">
        <div className="w-full max-w-md mx-auto space-y-6">
          <h2 className="text-xl md:text-2xl font-bold text-intellend-green text-center">
            Why Choose Intellend?
          </h2>

          {/* Features Grid */}
          <div className="grid grid-cols-2 gap-4">
            {/* Transparent Fee */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col items-center text-center space-y-3">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                <CreditCard className="h-6 w-6 text-intellend-green" />
              </div>
              <p className="text-sm md:text-base font-semibold text-gray-800">
                Transparent Fee
              </p>
            </div>

            {/* Daily Repayment */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col items-center text-center space-y-3">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                <Clock className="h-6 w-6 text-intellend-green" />
              </div>
              <p className="text-sm md:text-base font-semibold text-gray-800">
                Daily Repayment
              </p>
            </div>

            {/* Minimal Docs */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col items-center text-center space-y-3">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                <FileText className="h-6 w-6 text-intellend-green" />
              </div>
              <p className="text-sm md:text-base font-semibold text-gray-800">
                Minimal Docs
              </p>
            </div>

            {/* Fast Processing */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col items-center text-center space-y-3">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-intellend-green" />
              </div>
              <p className="text-sm md:text-base font-semibold text-gray-800">
                Fast Processing
              </p>
            </div>
          </div>

          {/* Apply Now Button */}
          <div className="pt-4">
            <Button
              onClick={handleApplyNow}
              disabled={disabled || isLoading}
              size="lg"
              className="w-full bg-intellend-green hover:bg-intellend-green/90 text-white py-6 text-base md:text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Loading...
                </>
              ) : (
                'Apply Now'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Landing;
